import React, { FC } from "react";
import classNames from "classnames";

/**
 * The 0–100 track (handoff §10, SCORE TRACK).
 *
 * The geometry comes from the scale, not from history: a track with hairline
 * ticks at 40 and 70 puts a single number in a position, so one reading already
 * has shape and a reason — you can see why 72 is Strong and that 68 sits just
 * under the line. Nothing implies a missing second point.
 *
 * Colour comes from `currentColor`. The caller wraps this in the band class
 * (`scoreCls`), so light and dark follow without a second banding table, and
 * the same wrapper colours the number beside it. Ticks are --surface knockouts;
 * the marker is a --surface centre with a band-colour ring.
 */
interface Props {
  /** 0–100. */
  value: number;
  /** Stage 2: a ghost marker at last month's value on the same track. */
  previous?: number | null;
  size?: "overall" | "sub";
  /** Labels under the track: 0/40/70/100, only 40/70, or none. */
  axis?: "full" | "ticks" | "none";
  className?: string;
}

const clamp = (n: number) => Math.max(0, Math.min(100, n));

const anchor = (t: number): string => {
  if (t === 0) return "none";
  if (t === 100) return "translateX(-100%)";
  return "translateX(-50%)";
};

export const ScoreTrack: FC<Props> = ({ value, previous, size = "overall", axis = "none", className }) => {
  const bar = size === "overall" ? 6 : 5;
  const marker = size === "overall" ? 14 : 12;
  const v = clamp(value);
  const p = previous == null ? null : clamp(previous);
  const labels = axis === "full" ? [0, 40, 70, 100] : [40, 70];

  return (
    <div className={classNames("w-full", className)}>
      <div
        className="relative w-full"
        style={{ height: bar, marginTop: (marker - bar) / 2 + 1, marginBottom: (marker - bar) / 2 + 1 }}
      >
        <div className="absolute inset-0 rounded-full bg-track" />
        <div className="absolute left-0 top-0 bottom-0 rounded-full" style={{ width: `${v}%`, background: "currentColor" }} />
        {[40, 70].map((t) => (
          <span
            key={t}
            className="absolute bg-surface"
            style={{ left: `${t}%`, top: -5, width: 1, height: bar + 10, transform: "translateX(-0.5px)" }}
            aria-hidden="true"
          />
        ))}
        {p != null && (
          <span
            className="absolute rounded-full"
            style={{
              left: `${p}%`,
              top: -3,
              width: 2,
              height: bar + 6,
              background: "currentColor",
              opacity: 0.35,
              transform: "translateX(-1px)",
            }}
            aria-hidden="true"
          />
        )}
        <span
          className="absolute rounded-full bg-surface"
          style={{
            left: `${v}%`,
            top: "50%",
            width: marker,
            height: marker,
            transform: "translate(-50%, -50%)",
            border: "3px solid currentColor",
            boxSizing: "border-box",
          }}
          aria-hidden="true"
        />
      </div>
      {axis !== "none" && (
        // --text-2, never --muted: these numbers carry the reading.
        <div className="relative text-text-2 tabular-nums" style={{ height: 12, marginTop: 4, fontSize: 9.5, lineHeight: 1 }}>
          {labels.map((t) => (
            <span
              key={t}
              // Below sm the axis keeps only 40 and 70.
              className={classNames("absolute", (t === 0 || t === 100) && "hidden sm:inline")}
              style={{ left: `${t}%`, transform: anchor(t) }}
            >
              {t}
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

export default ScoreTrack;
